import { useState, useEffect } from 'react';

/**
 * Branded loading screen / splash overlay for Fleetora
 * Shows while the application is initializing
 */
const LoadingScreen = ({ onComplete, loadingText = 'Initializing Fleetora...', minDisplayTime = 800, language = 'en' }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    // Ensure minimum display time for smooth UX
    const timer = setTimeout(() => {
      setIsFading(true);
      // Allow fade animation to complete before calling onComplete
      setTimeout(() => {
        setIsVisible(false);
        if (onComplete) onComplete();
      }, 400);
    }, minDisplayTime);

    return () => clearTimeout(timer);
  }, [minDisplayTime, onComplete]);

  if (!isVisible) return null;

  const subtitle = language === 'cs' 
    ? 'Provozní platforma pro UAS operátory' 
    : 'Operations platform for UAS operators';

  return (
    <div 
      className={`
        fixed inset-0 z-[9999] flex flex-col items-center justify-center
        transition-opacity duration-400 ease-out
        ${isFading ? 'opacity-0' : 'opacity-100'}
      `}
      style={{ background: '#0F1824' }}
      data-testid="loading-screen"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(30, 58, 95, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(78, 205, 196, 0.15) 0%, transparent 50%)`
        }} />
      </div>

      {/* Main content */}
      <div className="relative flex flex-col items-center">
        {/* Logo with pulse animation */}
        <div className="relative mb-6">
          {/* Outer glow ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className="rounded-full animate-pulse" 
              style={{ 
                width: '100px', 
                height: '100px',
                background: 'radial-gradient(circle, rgba(78, 205, 196, 0.2) 0%, transparent 70%)'
              }} 
            />
          </div>
          
          {/* Logo image - contains FLEETORA text */}
          <img 
            src={`${process.env.PUBLIC_URL}/brand/fleetora-logo-circle-2.png`}
            alt="Fleetora" 
            style={{ width: '80px', height: '80px' }}
            className="relative"
          />
        </div>

        {/* Subtitle only - logo already has Fleetora name */}
        <p className="text-sm mb-8 tracking-wide" style={{ color: '#8A9AA8' }}>
          {subtitle}
        </p>

        {/* Loading indicator */}
        <div className="flex flex-col items-center gap-4">
          {/* Animated dots */}
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full animate-[bounce_1s_ease-in-out_infinite]" style={{ background: '#4ECDC4', animationDelay: '0ms' }} />
            <div className="h-2 w-2 rounded-full animate-[bounce_1s_ease-in-out_infinite]" style={{ background: '#4ECDC4', animationDelay: '150ms' }} />
            <div className="h-2 w-2 rounded-full animate-[bounce_1s_ease-in-out_infinite]" style={{ background: '#4ECDC4', animationDelay: '300ms' }} />
          </div>

          {/* Loading text */}
          <p className="text-sm tracking-wide" style={{ color: '#6B7A88' }}>
            {loadingText}
          </p>
        </div>
      </div>

      {/* Footer branding */}
      <div className="absolute bottom-8 flex flex-col items-center">
        <p className="text-xs" style={{ color: '#4A5568' }}>
          by <span style={{ color: '#6B7A88' }} className="font-medium">Aeroway</span>
        </p>
      </div>
    </div>
  );
};

export default LoadingScreen;
